/**
 * Plan 25 — CafeteriaLauncher.
 *
 * Fixed-bottom sidebar action that drops a user directly into
 * `/cafeteria` (Cafeteria Mode — Plan 26). This is the SOLE entry
 * point to Cafeteria Mode: the canonical numbered nav item was
 * removed, so there is no "Cafeteria Mode" row in the sidebar. The
 * launcher is a compact primary action, always at the bottom of the
 * sidebar, so paired tablets surface "tap here to start service"
 * without the user having to scan the regular nav list.
 *
 * Tapping it clears the persisted campus lock BEFORE it navigates
 * (PROD-1302): the mode layout then asks which campus on every entry when
 * the worker has more than one, instead of landing on last time's. A
 * single permitted campus still auto-locks silently there, and a paired
 * tablet keeps its kiosk campus regardless.
 *
 * Visibility rules:
 *
 *   1. The page loaded under a kiosk bearer (`profile.kioskContext.paired
 *      === true`) — the tablet was paired and the bootstrap response
 *      surfaced the kiosk session. The launcher is the headline action.
 *
 *   2. The current user has a CAFETERIA_WORKER membership at the
 *      active school — they can drop into Cafeteria Mode without
 *      pairing the browser as a kiosk (e.g. a worker testing on
 *      their personal laptop). Same prominent button.
 *
 *   3. The current user has a SCHOOL_ADMIN membership — at most schools
 *      the admin IS the person who runs service, and the server already
 *      authorizes SCHOOL_ADMIN for /api/cafeteria/*. This covers the
 *      Ordo-takeover "Access School View" operator too: impersonation
 *      swaps the identity, so a school-view session simply reports
 *      SCHOOL_ADMIN here.
 *
 *   4. The current user is an ORDO_ADMIN — they walk customers through
 *      serve flows when debugging (the panopticon access the removed
 *      nav item used to give them).
 *
 *   5. The server reports `cafeteriaAccess.enabled` (PROD-1168) — a chef
 *      (CHEF_ADMIN / CHEF_STAFF) who works on site and holds a cafeteria
 *      grant the Ordo team issued in Customers for specific campuses. The
 *      campus picker then lists exactly those campuses.
 *
 *   6. Otherwise: hidden. A PARENT, a TEACHER, or a chef-side user
 *      without a grant doesn't see it.
 */

import { AppSidebarItem } from '@ordo/ui/app-sidebar'
import { ArrowRight, HandPlatter } from 'lucide-react'
import { useNavigate } from 'react-router'

import { clearCafeteriaLocationLock } from '@/api/cafeteria'
import { useProfile } from '@/stores/profile'

import { shouldShowCafeteriaLauncher } from './launcher.helpers'

export { shouldShowCafeteriaLauncher }

export function CafeteriaLauncher({ collapsed = false }: { collapsed?: boolean }) {
  const { profile } = useProfile()
  const navigate = useNavigate()

  if (!shouldShowCafeteriaLauncher(profile)) {
    return null
  }

  return (
    <ul className="space-y-0.5">
      <AppSidebarItem
        // No leading icon in the expanded view — the label + trailing arrow
        // carry the button. The icon is supplied only in the collapsed rail,
        // where AppSidebarItem renders the leading slot alone (and the
        // trailing arrow is dropped), so the row still has a glyph.
        icon={collapsed ? <HandPlatter className="h-5 w-5 md:h-4 md:w-4" /> : undefined}
        // Clear the remembered campus first, then go: the layout that mounts
        // on arrival must not see last time's lock (PROD-1302, see header).
        onClick={() => {
          clearCafeteriaLocationLock()
          navigate('/cafeteria/today')
        }}
        trailing={
          <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        }
        // `data-slot="button"` opts this row into the Ordo pill radius
        // (`--btn-radius`, applied app-wide via globals.css) so it reads as a
        // real Ordo button, not a plain sidebar row. Outline tokens match the
        // shared Button `variant="outline"`.
        data-slot="button"
        className="group border border-input bg-background px-3 font-medium text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground">
        Enter Cafeteria mode
      </AppSidebarItem>
    </ul>
  )
}
